import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState, ACTION_TYPES } from '../hooks/useAppState';
import { getTheme } from '../lib/theme';
import { calificarGuiaI, calificarGuiaII, calificarGuiaIII, determinarObligaciones } from '../lib/scoring';
import { construirInformeHTML } from '../lib/informe-html';
import { generarInformePDFServidor, marcarInformeGenerado } from '../lib/api';
import { COLORES_NIVEL, NIVELES_RIESGO_GII, NIVELES_CATEGORIA_GII, NIVELES_DOMINIO_GII, NIVELES_RIESGO_GIII, NIVELES_CATEGORIA_GIII, NIVELES_DOMINIO_GIII } from '../data';

const NIVELES = ['Nulo','Bajo','Medio','Alto','Muy alto'];

const ACCIONES = {
  'Nulo': 'El riesgo resulta despreciable por lo que no se requiere medidas adicionales.',
  'Bajo': 'Es necesario una mayor difusión de la política de prevención de riesgos psicosociales y programas para: la prevención de los factores de riesgo psicosocial, la promoción de un entorno organizacional favorable y la prevención de la violencia laboral.',
  'Medio': 'Se requiere revisar la política de prevención de riesgos psicosociales y programas para la prevención de los factores de riesgo psicosocial, la promoción de un entorno organizacional favorable y la prevención de la violencia laboral, así como reforzar su aplicación y difusión, mediante un Programa de intervención.',
  'Alto': 'Se requiere realizar un análisis de cada categoría y dominio, de manera que se puedan determinar las acciones de intervención apropiadas a través de un Programa de intervención, que podrá incluir una evaluación específica¹ y deberá incluir una campaña de sensibilización, revisar la política de prevención de riesgos psicosociales y programas para la prevención de los factores de riesgo psicosocial, la promoción de un entorno organizacional favorable y la prevención de la violencia laboral, así como reforzar su aplicación y difusión.',
  'Muy alto': 'Se requiere realizar el análisis de cada categoría y dominio para establecer las acciones de intervención apropiadas, mediante un Programa de intervención que deberá incluir evaluaciones específicas¹, y contemplar campañas de sensibilización, revisar la política de prevención de riesgos psicosociales y programas para la prevención de los factores de riesgo psicosocial, la promoción de un entorno organizacional favorable y la prevención de la violencia laboral, así como reforzar su aplicación y difusión.',
};

const EVALUACION_ESPECIFICA = '¹ Evaluación específica: Estudio a profundidad de los factores de riesgo psicosocial a través de instrumentos cuantitativos (cuestionarios), cualitativos (entrevistas) o mixtos y, en su caso, clínicos, capaces de evaluar el entorno organizacional y el efecto a la salud de los trabajadores. Por ejemplo: identificación del síndrome de estar quemado por el trabajo (burnout) o acoso psicológico (mobbing), entre otros.';

const MAPEO_73 = [
  { inciso: 'a)', requisito: 'Sentido de pertenencia de los trabajadores', dominio: 'Insuficiente sentido de pertenencia e, inestabilidad' },
  { inciso: 'b)', requisito: 'Formación para la adecuada realización de tareas', dominio: 'Falta de control sobre el trabajo' },
  { inciso: 'c)', requisito: 'Definición precisa de responsabilidades', dominio: 'Falta de control sobre el trabajo' },
  { inciso: 'd)', requisito: 'Participación proactiva y comunicación', dominio: 'Liderazgo' },
  { inciso: 'e)', requisito: 'Distribución adecuada de cargas de trabajo', dominio: 'Carga de trabajo' },
  { inciso: 'f)', requisito: 'Evaluación y reconocimiento del desempeño', dominio: 'Reconocimiento del desempeño' },
];

function getNivelFromPuntaje(puntaje, escalas) {
  if (!escalas || puntaje === null) return 'Nulo';
  for (const e of escalas) {
    if (puntaje >= e.min && puntaje <= e.max) return e.nivel;
  }
  return 'Nulo';
}

export default function ResultadosPage() {
  const { state, dispatch } = useAppState();
  const navigate = useNavigate();
  const [generandoPDF, setGenerandoPDF] = useState(false);
  const [errorPDF, setErrorPDF] = useState('');
  const [showFirmaModal, setShowFirmaModal] = useState(false);
  const [firmaNombre, setFirmaNombre] = useState('');
  const [firmaCargo, setFirmaCargo] = useState('');
  const [firmaCedula, setFirmaCedula] = useState('');
  const t = getTheme(state.darkMode);
  const dark = state.darkMode;
  const centro = state.centrosTrabajo[state.centroActualIdx];
  if (!centro) return null;

  const trabajadores = centro.trabajadores || [];
  const cuestionarios = centro.cuestionariosCompletos || {};
  const obligaciones = determinarObligaciones(centro.numTrabajadores);

  // ── Compute all individual results ────────────────────────
  const { resultadosGI, resultadosGII, resultadosGIII } = useMemo(() => {
    const rGI = [], rGII = [], rGIII = [];
    trabajadores.forEach(tw => {
      const r1raw = cuestionarios[`${tw.id}_guiaI`];
      const r1 = r1raw?.respuestas || r1raw;
      if (r1) rGI.push({ trabajador: tw, resultado: calificarGuiaI(r1) });
      const r2raw = cuestionarios[`${tw.id}_guiaII`];
      const r2 = r2raw?.respuestas || r2raw;
      if (r2) rGII.push({ trabajador: tw, resultado: calificarGuiaII(r2) });
      const r3raw = cuestionarios[`${tw.id}_guiaIII`];
      const r3 = r3raw?.respuestas || r3raw;
      if (r3) rGIII.push({ trabajador: tw, resultado: calificarGuiaIII(r3) });
    });
    return { resultadosGI: rGI, resultadosGII: rGII, resultadosGIII: rGIII };
  }, [trabajadores, cuestionarios]);

  const hayResultados = resultadosGI.length > 0 || resultadosGII.length > 0 || resultadosGIII.length > 0;

  // ── Aggregate GII by category and domain ──────────────────
  const aggGII = useMemo(() => {
    if (resultadosGII.length === 0) return null;
    const catAgg = {}, domAgg = {};
    let totalSum = 0;
    resultadosGII.forEach(r => {
      totalSum += r.resultado.totalFinal || 0;
      Object.entries(r.resultado.categorias || {}).forEach(([cat, data]) => {
        if (!catAgg[cat]) catAgg[cat] = { sum: 0, count: 0 };
        catAgg[cat].sum += data.puntaje; catAgg[cat].count++;
      });
      Object.entries(r.resultado.dominios || {}).forEach(([dom, data]) => {
        if (!domAgg[dom]) domAgg[dom] = { sum: 0, count: 0 };
        domAgg[dom].sum += data.puntaje; domAgg[dom].count++;
      });
    });
    const n = resultadosGII.length;
    const totalProm = Math.round(totalSum / n);
    const totalNivel = getNivelFromPuntaje(totalProm, NIVELES_RIESGO_GII);

    // Distribution by nivel
    const dist = { Nulo: 0, Bajo: 0, Medio: 0, Alto: 0, 'Muy alto': 0 };
    resultadosGII.forEach(r => { dist[r.resultado.nivelFinal] = (dist[r.resultado.nivelFinal] || 0) + 1; });

    const cats = Object.entries(catAgg).map(([cat, d]) => {
      const prom = Math.round(d.sum / d.count);
      return { nombre: cat, puntaje: prom, nivel: getNivelFromPuntaje(prom, NIVELES_CATEGORIA_GII[cat]) };
    });
    const doms = Object.entries(domAgg).map(([dom, d]) => {
      const prom = Math.round(d.sum / d.count);
      return { nombre: dom, puntaje: prom, nivel: getNivelFromPuntaje(prom, NIVELES_DOMINIO_GII[dom]) };
    });
    return { totalProm, totalNivel, dist, categorias: cats, dominios: doms, evaluados: n };
  }, [resultadosGII]);

  // ── Aggregate GIII by category and domain ─────────────────
  const aggGIII = useMemo(() => {
    if (resultadosGIII.length === 0) return null;
    const catAgg = {}, domAgg = {};
    let totalSum = 0;
    resultadosGIII.forEach(r => {
      totalSum += r.resultado.totalFinal || 0;
      Object.entries(r.resultado.categorias || {}).forEach(([cat, data]) => {
        if (!catAgg[cat]) catAgg[cat] = { sum: 0, count: 0 };
        catAgg[cat].sum += data.puntaje; catAgg[cat].count++;
      });
      Object.entries(r.resultado.dominios || {}).forEach(([dom, data]) => {
        if (!domAgg[dom]) domAgg[dom] = { sum: 0, count: 0 };
        domAgg[dom].sum += data.puntaje; domAgg[dom].count++;
      });
    });
    const n = resultadosGIII.length;
    const totalProm = Math.round(totalSum / n);
    const totalNivel = getNivelFromPuntaje(totalProm, NIVELES_RIESGO_GIII);

    const dist = { Nulo: 0, Bajo: 0, Medio: 0, Alto: 0, 'Muy alto': 0 };
    resultadosGIII.forEach(r => { dist[r.resultado.nivelFinal] = (dist[r.resultado.nivelFinal] || 0) + 1; });

    const cats = Object.entries(catAgg).map(([cat, d]) => {
      const prom = Math.round(d.sum / d.count);
      return { nombre: cat, puntaje: prom, nivel: getNivelFromPuntaje(prom, NIVELES_CATEGORIA_GIII[cat]) };
    });
    const doms = Object.entries(domAgg).map(([dom, d]) => {
      const prom = Math.round(d.sum / d.count);
      return { nombre: dom, puntaje: prom, nivel: getNivelFromPuntaje(prom, NIVELES_DOMINIO_GIII[dom]) };
    });
    return { totalProm, totalNivel, dist, categorias: cats, dominios: doms, evaluados: n };
  }, [resultadosGIII]);

  // ── Styles ────────────────────────────────────────────────
  const thS = { fontSize: '0.65rem', fontWeight: 700, padding: '8px 10px', color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `2px solid ${t.border}` };
  const tdS = { fontSize: '0.78rem', padding: '10px 12px', borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.04)' : '#f3f4f6'}` };

  const NivelBadge = ({ nivel, size = 'normal' }) => (
    <span style={{
      display: 'inline-block', padding: size === 'large' ? '5px 16px' : '3px 10px',
      borderRadius: 20, fontSize: size === 'large' ? '0.8rem' : '0.7rem', fontWeight: 700,
      background: `${COLORES_NIVEL[nivel]}18`, color: COLORES_NIVEL[nivel],
      border: `1px solid ${COLORES_NIVEL[nivel]}30`,
    }}>{nivel}</span>
  );

  const SectionTitle = ({ emoji, text, count }) => (
    <h2 style={{ fontSize: '1rem', fontWeight: 700, color: t.textStrong, margin: '0 0 16px', paddingBottom: 10, borderBottom: `2px solid rgba(99,102,241,0.2)`, display: 'flex', alignItems: 'center', gap: 8 }}>
      <span>{emoji}</span> {text} <span style={{ fontSize: '0.72rem', fontWeight: 400, color: t.textMuted }}>{count} evaluados</span>
    </h2>
  );

  const EmptyBox = ({ text }) => (
    <div style={{ padding: 24, background: dark ? 'rgba(255,255,255,0.02)' : '#f9fafb', borderRadius: 10, border: `1px dashed ${t.border}`, textAlign: 'center' }}>
      <p style={{ fontSize: '0.82rem', color: t.textFaint, margin: 0 }}>{text} <span style={{ color: t.accent, cursor: 'pointer' }} onClick={() => navigate('/cuestionarios')}>Aplicar cuestionarios →</span></p>
    </div>
  );

  // ── Distribution bar ──────────────────────────────────────
  const DistBar = ({ dist, total }) => (
    <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', height: 28, marginBottom: 12 }}>
      {NIVELES.map(n => {
        const count = dist[n] || 0;
        if (count === 0) return null;
        const pct = (count / total) * 100;
        return (
          <div key={n} title={`${n}: ${count} (${Math.round(pct)}%)`} style={{
            width: `${pct}%`, background: COLORES_NIVEL[n], display: 'flex',
            alignItems: 'center', justifyContent: 'center', minWidth: count > 0 ? 30 : 0,
          }}>
            <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#fff' }}>{count}</span>
          </div>
        );
      })}
    </div>
  );

  // ── Results table (categories or domains) ─────────────────
  const ResultsTable = ({ rows, title }) => (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: t.accent, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{title}</div>
      <div style={{ borderRadius: 10, border: `1px solid ${t.border}`, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: dark ? 'rgba(255,255,255,0.03)' : '#f9fafb' }}>
              <th style={{ ...thS, textAlign: 'left' }}>Parámetro</th>
              <th style={{ ...thS, textAlign: 'center', width: 80 }}>Puntaje</th>
              <th style={{ ...thS, textAlign: 'center', width: 100 }}>Nivel</th>
              <th style={{ ...thS, textAlign: 'left' }}>Acción requerida</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const isHigh = row.nivel === 'Alto' || row.nivel === 'Muy alto';
              return (
                <tr key={i} style={{ background: isHigh ? `${COLORES_NIVEL[row.nivel]}06` : 'transparent' }}>
                  <td style={{ ...tdS, fontWeight: 600, color: t.textStrong }}>{row.nombre}</td>
                  <td style={{ ...tdS, textAlign: 'center', fontWeight: 700, color: COLORES_NIVEL[row.nivel] }}>{row.puntaje}</td>
                  <td style={{ ...tdS, textAlign: 'center' }}><NivelBadge nivel={row.nivel} /></td>
                  <td style={{ ...tdS, fontSize: '0.72rem', color: isHigh ? COLORES_NIVEL[row.nivel] : t.textMuted }}>
                    {isHigh ? '⚠️ Programa de intervención' : row.nivel === 'Medio' ? '⚡ Reforzar política' : '✓ Sin acción'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ── Aggregate results card ────────────────────────────────
  const GuiaAggCard = ({ agg, guiaLabel, emoji, title }) => {
    if (!agg) return <EmptyBox text={`Sin evaluaciones de ${title}.`} />;
    return (
      <div>
        {/* Overall result */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px', background: dark ? 'rgba(255,255,255,0.02)' : '#fff', borderRadius: 12, border: `1px solid ${t.border}`, marginBottom: 16 }}>
          <div style={{ textAlign: 'center', minWidth: 80 }}>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: COLORES_NIVEL[agg.totalNivel] }}>{agg.totalProm}</div>
            <div style={{ fontSize: '0.65rem', color: t.textMuted, fontWeight: 600 }}>Promedio</div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <NivelBadge nivel={agg.totalNivel} size="large" />
              <span style={{ fontSize: '0.82rem', color: t.textStrong, fontWeight: 600 }}>Nivel general del centro</span>
            </div>
            <div style={{ fontSize: '0.75rem', color: t.textMuted, lineHeight: 1.5 }}>
              {ACCIONES[agg.totalNivel]}
            </div>
            {(agg.totalNivel === 'Alto' || agg.totalNivel === 'Muy alto') && (
              <div style={{ fontSize: '0.68rem', color: t.textFaint, lineHeight: 1.4, marginTop: 8, fontStyle: 'italic' }}>
                {EVALUACION_ESPECIFICA}
              </div>
            )}
          </div>
        </div>

        {/* Distribution */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 600, color: t.textMuted, marginBottom: 6 }}>Distribución de trabajadores por nivel</div>
          <DistBar dist={agg.dist} total={agg.evaluados} />
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {NIVELES.map(n => {
              const count = agg.dist[n] || 0;
              if (count === 0) return null;
              return (
                <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.72rem' }}>
                  <div style={{ width: 10, height: 10, borderRadius: 3, background: COLORES_NIVEL[n] }} />
                  <span style={{ color: t.textMuted }}>{n}: <strong style={{ color: t.textStrong }}>{count}</strong> ({Math.round(count / agg.evaluados * 100)}%)</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Categories */}
        <ResultsTable rows={agg.categorias} title="Resultados por categoría" />

        {/* Domains */}
        <ResultsTable rows={agg.dominios} title="Resultados por dominio" />
      </div>
    );
  };

  // ── GI Aggregate ──────────────────────────────────────────
  const GIAggregate = () => {
    if (resultadosGI.length === 0) return <EmptyBox text="Sin evaluaciones de Guía I." />;
    const conAtencion = resultadosGI.filter(r => r.resultado.requiereAtencion).length;
    const sinAtencion = resultadosGI.length - conAtencion;
    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
          <div style={{ padding: '16px 20px', background: dark ? 'rgba(255,255,255,0.02)' : '#fff', borderRadius: 12, border: `1px solid ${t.border}`, textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: t.textStrong }}>{resultadosGI.length}</div>
            <div style={{ fontSize: '0.75rem', color: t.textMuted }}>Evaluados</div>
          </div>
          <div style={{ padding: '16px 20px', background: sinAtencion > 0 ? 'rgba(16,185,129,0.06)' : (dark ? 'rgba(255,255,255,0.02)' : '#f9fafb'), borderRadius: 12, border: `1px solid ${sinAtencion > 0 ? 'rgba(16,185,129,0.2)' : t.border}`, textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: '#10b981' }}>{sinAtencion}</div>
            <div style={{ fontSize: '0.75rem', color: '#10b981' }}>Sin atención requerida</div>
          </div>
          <div style={{ padding: '16px 20px', background: conAtencion > 0 ? 'rgba(239,68,68,0.06)' : (dark ? 'rgba(255,255,255,0.02)' : '#f9fafb'), borderRadius: 12, border: `1px solid ${conAtencion > 0 ? 'rgba(239,68,68,0.2)' : t.border}`, textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: conAtencion > 0 ? '#ef4444' : t.textFaint }}>{conAtencion}</div>
            <div style={{ fontSize: '0.75rem', color: conAtencion > 0 ? '#ef4444' : t.textMuted }}>Requieren atención clínica</div>
          </div>
        </div>
        {conAtencion > 0 && (
          <div style={{ padding: '12px 16px', background: 'rgba(239,68,68,0.06)', borderRadius: 10, border: '1px solid rgba(239,68,68,0.15)', marginBottom: 12 }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#ef4444', marginBottom: 4 }}>⚠️ Trabajadores con ATS que requieren atención:</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {resultadosGI.filter(r => r.resultado.requiereAtencion).map((r, i) => (
                <span key={i} style={{ fontSize: '0.72rem', padding: '3px 10px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontWeight: 500 }}>
                  {r.trabajador.nombreCompleto || r.trabajador.nombre}
                </span>
              ))}
            </div>
          </div>
        )}
        <div style={{ fontSize: '0.78rem', color: t.textMuted, lineHeight: 1.5, padding: '12px 16px', background: dark ? 'rgba(255,255,255,0.02)' : '#f9fafb', borderRadius: 10 }}>
          {conAtencion > 0
            ? 'Se identificaron trabajadores expuestos a acontecimientos traumáticos severos que requieren valoración clínica y, en su caso, canalización conforme al numeral 5.5 de la NOM-035.'
            : 'No se identificaron trabajadores que requieran valoración clínica por acontecimientos traumáticos severos.'}
        </div>
      </div>
    );
  };

  // ── Entorno Organizacional 7.3 ────────────────────────────
  const EntornoOrganizacional = () => {
    if (!aggGIII) return null;
    const domMap = {};
    aggGIII.dominios.forEach(d => { domMap[d.nombre] = d; });
    return (
      <div style={{ marginBottom: 28 }}>
        <SectionTitle emoji="🏢" text="Entorno Organizacional — Numeral 7.3" count={aggGIII.evaluados} />
        <div style={{ borderRadius: 10, border: `1px solid ${t.border}`, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: dark ? 'rgba(255,255,255,0.03)' : '#f9fafb' }}>
                <th style={{ ...thS, textAlign: 'left', width: 40 }}>Inciso</th>
                <th style={{ ...thS, textAlign: 'left' }}>Requisito NOM-035</th>
                <th style={{ ...thS, textAlign: 'left' }}>Dominio GIII</th>
                <th style={{ ...thS, textAlign: 'center', width: 80 }}>Promedio</th>
                <th style={{ ...thS, textAlign: 'center', width: 90 }}>Nivel</th>
              </tr>
            </thead>
            <tbody>
              {MAPEO_73.map(m => {
                const dom = domMap[m.dominio] || { puntaje: 0, nivel: 'Nulo' };
                const isHigh = dom.nivel === 'Alto' || dom.nivel === 'Muy alto';
                return (
                  <tr key={m.inciso} style={{ background: isHigh ? `${COLORES_NIVEL[dom.nivel]}06` : 'transparent' }}>
                    <td style={{ ...tdS, fontWeight: 700, color: t.accent }}>{m.inciso}</td>
                    <td style={{ ...tdS, fontWeight: 500, color: t.textStrong }}>{m.requisito}</td>
                    <td style={{ ...tdS, fontSize: '0.72rem', color: t.textMuted }}>{m.dominio}</td>
                    <td style={{ ...tdS, textAlign: 'center', fontWeight: 700, color: COLORES_NIVEL[dom.nivel] }}>{dom.puntaje}</td>
                    <td style={{ ...tdS, textAlign: 'center' }}><NivelBadge nivel={dom.nivel} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: '1.3rem', fontWeight: 700, color: t.textStrong, margin: 0 }}>Resultados</h1>
          <p style={{ color: t.textMuted, fontSize: '0.85rem', margin: '4px 0 0' }}>{centro.nombreCentro} · Análisis grupal conforme a las tablas oficiales NOM-035-STPS-2018</p>
        </div>
        {(() => {
          const programa = centro.programaIntervencion || {};
          const tienePrograma = (programa.acciones || []).length > 0;
          const puedeGenerar = hayResultados && tienePrograma;
          const abrirFirmaModal = () => {
            setFirmaNombre(state.empresa?.responsableEvaluacion || state.empresa?.representanteLegal || '');
            setFirmaCargo(state.empresa?.cargoResponsable || state.empresa?.cargoRepresentante || '');
            setFirmaCedula(state.empresa?.cedulaResponsable || '');
            setShowFirmaModal(true);
          };
          const handleGenerarInforme = async (nombre, cargo, cedula) => {
            setErrorPDF('');
            setGenerandoPDF(true);
            try {
              // Persiste los datos de firma en la empresa para que la próxima vez
              // ya vengan precargados (y para que el reporte quede consistente
              // con lo declarado, no solo con lo que se usó en este PDF puntual).
              dispatch({ type: ACTION_TYPES.SET_EMPRESA, payload: { ...state.empresa, responsableEvaluacion: nombre, cargoResponsable: cargo, cedulaResponsable: cedula } });

              const rGIraw = {};
              trabajadores.forEach(tw => { const r = cuestionarios[`${tw.id}_guiaI`]; if (r) rGIraw[tw.id] = r?.respuestas || r; });
              const html = construirInformeHTML({
                empresa: state.empresa,
                centro,
                rGI: resultadosGI.map(r => ({ nombre: r.trabajador.nombreCompleto, requiere: r.resultado.requiereAtencion, resultado: r.resultado })),
                rGII: resultadosGII.map(r => ({ nombre: r.trabajador.nombreCompleto, nivel: r.resultado.nivelFinal, totalFinal: r.resultado.totalFinal, dominios: r.resultado.dominios, categorias: r.resultado.categorias })),
                rGIII: resultadosGIII.map(r => ({ nombre: r.trabajador.nombreCompleto, nivel: r.resultado.nivelFinal, totalFinal: r.resultado.totalFinal, dominios: r.resultado.dominios, categorias: r.resultado.categorias })),
                fichas: centro.fichas || {},
                rGIraw,
                programa,
                responsable: nombre,
                cargo,
                cedula,
              });
              const blob = await generarInformePDFServidor(html);
              const url = URL.createObjectURL(blob);
              const nombreArchivo = 'Informe_NOM035_' + (state.empresa?.razonSocial || state.empresa?.nombre || 'Empresa').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30) + '.pdf';
              const link = document.createElement('a');
              link.href = url;
              link.download = nombreArchivo;
              link.style.display = 'none';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
              marcarInformeGenerado(centro.id);
            } catch (e) {
              setErrorPDF(e.message || 'Error generando el informe PDF');
            } finally {
              setGenerandoPDF(false);
            }
          };
          return (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                <button disabled={!puedeGenerar || generandoPDF} onClick={abrirFirmaModal} style={{
                  padding: '10px 20px', background: (puedeGenerar && !generandoPDF) ? t.accent : (dark ? '#333' : '#e5e7eb'),
                  color: (puedeGenerar && !generandoPDF) ? '#fff' : t.textFaint, border: 'none', borderRadius: 10,
                  fontSize: '0.85rem', fontWeight: 600, cursor: (puedeGenerar && !generandoPDF) ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  {generandoPDF ? '⏳ Generando informe…' : '📄 Generar informe PDF'}
                </button>
                {hayResultados && !tienePrograma && (
                  <span style={{ fontSize: '0.7rem', color: '#f59e0b' }}>⚠ Genera el Programa de intervención primero</span>
                )}
                {errorPDF && (
                  <span style={{ fontSize: '0.7rem', color: '#ef4444', maxWidth: 260, textAlign: 'right' }}>{errorPDF}</span>
                )}
              </div>

              {showFirmaModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }} onClick={() => setShowFirmaModal(false)}>
                  <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()}>
                    <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: t.textStrong, margin: '0 0 6px' }}>Datos del responsable de la evaluación</h3>
                    <p style={{ fontSize: '0.78rem', color: t.textMuted, margin: '0 0 18px' }}>Numeral 7.7 inciso h) de la NOM-035 — se guardan para este y futuros informes.</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div>
                        <label style={{ fontSize: '0.72rem', fontWeight: 600, color: t.textMuted, marginBottom: 4, display: 'block' }}>Nombre completo *</label>
                        <input value={firmaNombre} onChange={e => setFirmaNombre(e.target.value)} placeholder="Ej. Ing. Alejandro Fuentes Navarro"
                          style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${t.borderInput || t.border}`, background: t.bgInput || (dark ? 'rgba(255,255,255,0.04)' : '#fff'), color: t.textStrong, fontSize: '0.85rem', boxSizing: 'border-box' }} />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.72rem', fontWeight: 600, color: t.textMuted, marginBottom: 4, display: 'block' }}>Cargo</label>
                        <input value={firmaCargo} onChange={e => setFirmaCargo(e.target.value)} placeholder="Ej. Responsable de Seguridad e Higiene"
                          style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${t.borderInput || t.border}`, background: t.bgInput || (dark ? 'rgba(255,255,255,0.04)' : '#fff'), color: t.textStrong, fontSize: '0.85rem', boxSizing: 'border-box' }} />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.72rem', fontWeight: 600, color: t.textMuted, marginBottom: 4, display: 'block' }}>Cédula profesional</label>
                        <input value={firmaCedula} onChange={e => setFirmaCedula(e.target.value)} placeholder="Si aplica — se omite si se deja en blanco"
                          style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${t.borderInput || t.border}`, background: t.bgInput || (dark ? 'rgba(255,255,255,0.04)' : '#fff'), color: t.textStrong, fontSize: '0.85rem', boxSizing: 'border-box' }} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
                      <button onClick={() => setShowFirmaModal(false)} style={{ flex: 1, padding: '11px', borderRadius: 10, border: `1px solid ${t.border}`, background: 'transparent', color: t.textMuted, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>Cancelar</button>
                      <button
                        disabled={!firmaNombre.trim()}
                        onClick={() => { setShowFirmaModal(false); handleGenerarInforme(firmaNombre.trim(), firmaCargo.trim(), firmaCedula.trim()); }}
                        style={{ flex: 2, padding: '11px', borderRadius: 10, border: 'none', background: firmaNombre.trim() ? t.accent : (dark ? '#333' : '#e5e7eb'), color: firmaNombre.trim() ? '#fff' : t.textFaint, fontSize: '0.85rem', fontWeight: 700, cursor: firmaNombre.trim() ? 'pointer' : 'not-allowed' }}>
                        Generar PDF
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </div>

      {/* Guía I */}
      <div style={{ marginBottom: 28 }}>
        <SectionTitle emoji="🩺" text="Guía I — Acontecimientos traumáticos severos" count={resultadosGI.length} />
        <GIAggregate />
      </div>

      {/* Guía II */}
      {obligaciones.guias.includes("II") && (
        <div style={{ marginBottom: 28 }}>
          <SectionTitle emoji="📋" text="Guía II — Factores de riesgo psicosocial" count={resultadosGII.length} />
          <GuiaAggCard agg={aggGII} guiaLabel="gii" emoji="📋" title="Guía II" />
        </div>
      )}

      {/* Guía III */}
      {obligaciones.nivel >= 3 && (
        <div style={{ marginBottom: 28 }}>
          <SectionTitle emoji="📊" text="Guía III — Factores de riesgo y entorno organizacional" count={resultadosGIII.length} />
          <GuiaAggCard agg={aggGIII} guiaLabel="giii" emoji="📊" title="Guía III" />
        </div>
      )}

      {/* Entorno Organizacional 7.3 */}
      {obligaciones.nivel >= 3 && <EntornoOrganizacional />}
    </div>
  );
}
